import React, { useMemo } from "react";

import { cn } from "@/lib/utils";

import { CommandGroup } from "cmdk";

import {
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
  CommandRoot,
} from "@/components/ui/Command";
import { type Icon, LoaderCircle } from "@/components/ui/Icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { Skeleton } from "@/components/ui/Skeleton";
import { BodyText } from "@/components/ui/Text";
import type { ItemOptionProps } from "./types";

const searchSizes: Record<string, string> = {
  "200": "max-h-[200px]",
  "400": "max-h-[400px]",
};

interface SearchProps {
  "aria-invalid"?: boolean;
  "aria-label"?: string;
  allowFreeText?: boolean;
  alwaysShowFreetextOption?: boolean;
  autoFocusInput?: boolean;
  autoFocusInputDelay?: number;
  clearOnSelect?: boolean;
  disabled?: boolean;
  freeTextPrefix?: string;
  input?: string;
  id?: string;
  isLoading?: boolean;
  leftIcon?: Icon;
  leftIconClassName?: string;
  manualFilter?: (
    options: ItemOptionProps[],
    searchString: string,
  ) => Array<ItemOptionProps>;
  minLength?: number;
  noResultsText?: string;
  onInputChange?: (searchString: string) => void;
  onItemSelected: (item: ItemOptionProps) => void;
  openOnFocus?: boolean;
  openWithoutInput?: boolean;
  options: Array<ItemOptionProps>;
  placeholder?: string;
  resetSelectedOnBlur?: boolean;
  shouldFilter?: boolean;
  showLoadingSpinner?: boolean;
  size?: string;
  value?: string;
  maxOptions?: number;
}

export const Search = React.forwardRef<HTMLInputElement, SearchProps>(
  (
    {
      "aria-invalid": ariaInvalid,
      "aria-label": ariaLabel,
      allowFreeText = false,
      alwaysShowFreetextOption = false,
      autoFocusInput = false,
      autoFocusInputDelay,
      clearOnSelect = false,
      disabled = false,
      freeTextPrefix = "Add",
      input = "",
      id,
      isLoading = false,
      leftIcon,
      leftIconClassName,
      manualFilter,
      minLength = 0,
      noResultsText = "No results found.",
      onInputChange = () => {},
      onItemSelected,
      openOnFocus = false,
      openWithoutInput = false,
      options = [],
      placeholder = "Search...",
      resetSelectedOnBlur = false,
      shouldFilter = false,
      showLoadingSpinner = false,
      size = "200",
      value = "",
      maxOptions = -1,
    },
    forwardedRef,
  ) => {
    const [inputValue, setInputValue] = React.useState<string>(input);
    const [showDropdownMenu, setShowDropdownMenu] = React.useState(false);
    const [lastSelectedItem, setLastSelectedItem] =
      React.useState<ItemOptionProps | null>(null);
    const [freetextItem, setFreetextItem] =
      React.useState<ItemOptionProps | null>(null);
    const internalRef = React.useRef<HTMLInputElement>(null);
    const justSelectedRef = React.useRef(false);

    // Use useImperativeHandle to properly handle both refs
    React.useImperativeHandle(
      forwardedRef,
      () => internalRef.current as HTMLInputElement,
      [],
    );

    React.useEffect(() => {
      if (!autoFocusInput || !autoFocusInputDelay || !internalRef.current) {
        return;
      }

      // Initial delay before starting focus attempts
      const initialTimer = setTimeout(() => {
        // Keep trying to focus every 25ms for up to 100ms
        const retryInterval = setInterval(() => {
          if (internalRef.current) {
            internalRef.current.focus();
            if (document.activeElement === internalRef.current) {
              clearInterval(retryInterval);
            }
          }
        }, 25);

        // Cleanup interval after 100ms
        setTimeout(() => clearInterval(retryInterval), 100);
      }, autoFocusInputDelay);

      return () => {
        clearTimeout(initialTimer);
      };
    }, [autoFocusInput, autoFocusInputDelay]);

    const handleInputChange = (searchString: string) => {
      setInputValue(searchString);
      onInputChange(searchString);
      setShowDropdownMenu(!!searchString || openWithoutInput);
    };

    // Prevent the dropdown from reopening after selecting an item. e.preventDefault()
    // on the item's mousedown isn't reliable in headless browsers. If it lets the event
    // through, focus can be returned to the input when the popover closes, firing onFocus
    // and reopening it.
    const blockReopen = () => {
      justSelectedRef.current = true;
      setTimeout(() => {
        justSelectedRef.current = false;
      }, 50);
    };

    const handleOnSelect = (item: ItemOptionProps) => {
      blockReopen();
      setInputValue(item.label);
      onItemSelected(item);
      setShowDropdownMenu(false);
      setLastSelectedItem(item);
      if (clearOnSelect) {
        setInputValue("");
        onInputChange("");
      }
    };

    const handleSelectFreeText = (item: ItemOptionProps) => {
      blockReopen();
      setInputValue(item.label);
      setFreetextItem(item);
      onItemSelected(item);
      setShowDropdownMenu(false);
      setLastSelectedItem(item);
      if (clearOnSelect) {
        setInputValue("");
        onInputChange("");
      }
    };

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Escape") {
          setShowDropdownMenu(false);
          if (resetSelectedOnBlur && lastSelectedItem) {
            setInputValue(lastSelectedItem.label);
            onInputChange(lastSelectedItem.label);
          } else if (resetSelectedOnBlur) {
            setInputValue("");
            onInputChange("");
          }
        }
      },
      [lastSelectedItem, onInputChange, resetSelectedOnBlur],
    );

    const combinedOptions = useMemo(
      () => (freetextItem ? [freetextItem, ...options] : options),
      [freetextItem, options],
    );

    const shouldUseCommandFilter = !isLoading && shouldFilter && !manualFilter;

    const optionsToRender = useMemo(() => {
      if (maxOptions === 0) {
        return [];
      }
      let options = combinedOptions;

      if (manualFilter) {
        options = manualFilter(combinedOptions, inputValue);
      }

      if (maxOptions > 0) {
        options = options.slice(0, maxOptions);
      }

      return options;
    }, [combinedOptions, inputValue, manualFilter, maxOptions]);

    return (
      <Popover open={showDropdownMenu}>
        <CommandRoot label={ariaLabel} shouldFilter={shouldUseCommandFilter}>
          <PopoverTrigger asChild>
            <CommandInput
              ref={internalRef}
              aria-invalid={ariaInvalid}
              aria-label={ariaLabel}
              autoFocus={autoFocusInput}
              id={id}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              onValueChange={handleInputChange}
              onMouseDown={() => setShowDropdownMenu(true)}
              value={inputValue}
              disabled={disabled}
              leftIcon={
                showLoadingSpinner && isLoading ? LoaderCircle : leftIcon
              }
              leftIconClassName={cn(
                leftIconClassName,
                showLoadingSpinner && isLoading && "animate-spin",
              )}
              onFocus={() => {
                if (justSelectedRef.current) {
                  return;
                }
                if (
                  !disabled &&
                  (value ||
                    openOnFocus ||
                    inputValue ||
                    combinedOptions?.length)
                ) {
                  setShowDropdownMenu(true);
                }
              }}
              data-testid="search-input"
            />
          </PopoverTrigger>
          <PopoverContent
            asChild
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              if (
                e.target instanceof Element &&
                e.target.hasAttribute("cmdk-input")
              ) {
                e.preventDefault();
              } else {
                setShowDropdownMenu(false);
                if (resetSelectedOnBlur && lastSelectedItem) {
                  setInputValue(lastSelectedItem.label);
                  onInputChange(lastSelectedItem.label);
                } else if (resetSelectedOnBlur) {
                  setInputValue("");
                  onInputChange("");
                }
              }
            }}
            className={cn(
              "p-0",
              leftIcon
                ? "w-[calc(var(--radix-popover-trigger-width)+2.5rem)] -ml-8"
                : "w-[calc(var(--radix-popover-trigger-width)+1rem)] -ml-2",
            )}
          >
            <CommandList>
              {!isLoading && !allowFreeText && (
                <CommandEmpty
                  noResultsText={noResultsText}
                  enteredLength={inputValue?.length}
                  minLength={minLength}
                />
              )}
              <CommandGroup>
                {isLoading && (
                  <CommandLoading>
                    <Skeleton className="h-3 w-[98%] m-2" />
                    <Skeleton className="h-3 w-[75%] m-2" />
                  </CommandLoading>
                )}
                {!isLoading &&
                  (optionsToRender.length > 0 ||
                    (allowFreeText && inputValue?.trim())) && (
                    <div
                      className={cn(
                        `${size in searchSizes ? searchSizes[size] : searchSizes["200"]} overflow-y-auto overflow-x-hidden border border-secondary rounded-md bg-white w-full`,
                      )}
                    >
                      <div className="px-1 py-0.5">
                        {optionsToRender.map((item: ItemOptionProps) => (
                          <CommandItem
                            key={item.value}
                            value={item.label}
                            keywords={[item.label]}
                            variant={item.variant || "default"}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              justSelectedRef.current = true;
                              // Handles edge case of clicking down, but not selecting an item
                              // i.e. mouse down -> dragging out -> mouse up
                              setTimeout(() => {
                                justSelectedRef.current = false;
                              }, 500);
                            }}
                            onSelect={() => {
                              handleOnSelect(item);
                            }}
                            disabled={item.disabled}
                            data-testid="search-option"
                          >
                            <div className="flex flex-1 w-full">
                              <BodyText
                                size="sm"
                                className="overflow-hidden overflow-ellipsis flex-1 cursor-pointer"
                                style={
                                  item.level
                                    ? { paddingLeft: `${item.level * 1}rem` }
                                    : {}
                                }
                              >
                                {item.label}
                              </BodyText>
                              {item.endLabel && (
                                <BodyText
                                  size="sm"
                                  textColor={
                                    item.variant === "warning"
                                      ? "warning"
                                      : "primary"
                                  }
                                  className="overflow-hidden overflow-ellipsis"
                                  style={
                                    item.level
                                      ? { paddingLeft: `${item.level * 1}rem` }
                                      : {}
                                  }
                                >
                                  {item.endLabel}
                                </BodyText>
                              )}
                            </div>
                            {item.subLabel && (
                              <BodyText
                                size="xs"
                                textColor="secondary"
                                className="overflow-hidden overflow-ellipsis"
                                style={
                                  item.level
                                    ? { paddingLeft: `${item.level * 1}rem` }
                                    : {}
                                }
                              >
                                {item.subLabel}
                              </BodyText>
                            )}
                          </CommandItem>
                        ))}
                        {allowFreeText &&
                          inputValue?.trim() &&
                          inputValue !== freetextItem?.label &&
                          (!combinedOptions?.find(
                            (option) =>
                              inputValue?.toLowerCase().trim() ===
                              option.label.toLowerCase().trim(),
                          ) ||
                            alwaysShowFreetextOption) && (
                            <CommandItem
                              key={inputValue}
                              value={inputValue}
                              keywords={[inputValue]}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                justSelectedRef.current = true;
                                // Handles edge case of clicking down, but not selecting an item
                                // i.e. mouse down -> dragging out -> mouse up
                                setTimeout(() => {
                                  justSelectedRef.current = false;
                                }, 500);
                              }}
                              onSelect={() => {
                                handleSelectFreeText({
                                  label: inputValue,
                                  value: inputValue,
                                  isFreeText: true,
                                });
                              }}
                            >
                              <div className="overflow-x-hidden flex flex-1">
                                <BodyText
                                  size="sm"
                                  className="truncate mr-2 flex-1 w-fit min-w-fit"
                                >
                                  {noResultsText}
                                </BodyText>
                                <BodyText
                                  size="sm"
                                  textColor="link"
                                  bold
                                  className="truncate"
                                >
                                  {`${freeTextPrefix} "${inputValue}"`}
                                </BodyText>
                              </div>
                            </CommandItem>
                          )}
                      </div>
                    </div>
                  )}
              </CommandGroup>
            </CommandList>
          </PopoverContent>
        </CommandRoot>
      </Popover>
    );
  },
);

Search.displayName = "Search";
